import { useState } from "react";
import { View, Text, ScrollView } from "react-native";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  FileCheck2,
  FileQuestion,
  FileText,
  Plus,
  Receipt,
  Wallet,
} from "lucide-react-native";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import {
  CardSubtitleText,
  CardSurface,
  MoonFilterChip,
  MoonSearchInput,
  Row,
  SectionTitleRow,
  BackLink,
  CompactActionButton,
  EmptyState,
  DialogIcon,
} from "@louise/moon-ui";
import { SCREEN_PADDING_TOP, colors } from "@louise/moon-ui/theme/tokens";
import { formatCurrency, useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  useCreatePaymentRecord,
  useDeletePaymentRecord,
  usePaymentRecords,
  useUpdatePaymentRecord,
  type CreatePaymentRecordPayload,
} from "@src/packages/costs/hooks/usePaymentRecords";
import { EMBER } from "../../../onboarding/theme";
import paymentEmptyIllustration from "../../../assets/images/tax-expense-receipt.png";
import StatusBadge from "../../components/StatusBadge";
import type {
  CostPaymentRecordParam,
  CostPaymentRecordStatus,
  CostsStackParamList,
  MainTabParamList,
} from "../../types";
import PaymentRecordDetailModal, {
  buildPaymentRecordChatPrompt,
} from "./PaymentRecordDetailModal";
import { useSmartBack } from "../../hooks/useSmartBack";
import {
  apiToUiPaymentRecord,
  parseDateText,
  todayIsoDate,
  uiToPaymentRecordUpdates,
} from "./paymentRecordMapping";
import {
  CostDocumentUploadModal,
  type ManualPaymentEntry,
  type ManualPaymentKind,
  type UploadedCostDocument,
} from "./suggested/CostsOverviewConceptScreen";
import ScreenTitle from "../../components/ScreenTitle";
import PaymentEntryTypePicker from "../../components/PaymentEntryTypePicker";
import {
  filterPaymentRecords,
  getPaymentRecordKind,
  type PaymentKindFilter,
  type PaymentStatusFilter,
} from "./paymentRecordFilters";
import { summarizePaymentRecords } from "./paymentRecordSummary";
import {
  hasCostDocumentUpload,
  mergeCostDocumentReferences,
  toAttachedCostDocumentReference,
} from "@src/packages/costs/document-linkage";
import { toDecimalAmount } from "@src/packages/costs/decimalAmount";
import { semanticColors } from "@louise/theme/tokens";
import { detachDocumentFromPaymentRecord } from "./taxReviewHelpers";

type Props = NativeStackScreenProps<CostsStackParamList, "Payments">;
const PAYMENT_ROW_ICON_COLOR = colors.greyBrown;
const PAYMENT_SPLIT_COLORS = {
  paid: "#F4B34F",
  claims: "#5B332C",
  left: "#B76545",
} as const;

type StatusBadgeTone =
  "neutral" | "gold" | "coral" | "green" | "orange" | "blue" | "purple";

function getPaymentToneStyle(status: CostPaymentRecordStatus): {
  label: string;
  color: string;
  badgeTone: StatusBadgeTone;
} {
  if (status === "paid") {
    return {
      label: "Paid",
      color: EMBER.colors.green,
      badgeTone: "green",
    };
  }
  if (status === "submitted") {
    return {
      label: "Submitted",
      color: colors.greyBrown,
      badgeTone: "blue",
    };
  }
  if (status === "waiting-eob") {
    return {
      label: "Waiting for EOB",
      color: colors.greyBrown,
      badgeTone: "neutral",
    };
  }
  if (status === "needs-receipt") {
    return {
      label: "Needs receipt",
      color: EMBER.colors.gold,
      badgeTone: "gold",
    };
  }
  if (status === "partially-covered") {
    return {
      label: "Partially covered",
      color: EMBER.colors.gold,
      badgeTone: "gold",
    };
  }
  if (status === "ready-reimbursement") {
    return {
      label: "Ready for claim",
      color: colors.greyBrown,
      badgeTone: "green",
    };
  }
  if (status === "denied") {
    return {
      label: "Denied",
      color: EMBER.colors.coral,
      badgeTone: "coral",
    };
  }
  if (status === "review") {
    return {
      label: "Needs match",
      color: EMBER.colors.gold,
      badgeTone: "gold",
    };
  }
  return {
    label: "Left to pay",
    color: EMBER.colors.coral,
    badgeTone: "coral",
  };
}

function renderStatusBadge(label: string, tone: StatusBadgeTone) {
  return (
    <View style={{ alignSelf: "flex-start" }}>
      <StatusBadge label={label} tone={tone} />
    </View>
  );
}

function shouldShowPaymentStatusBadge(status: CostPaymentRecordStatus) {
  return (
    status === "submitted" ||
    status === "waiting-eob" ||
    status === "denied" ||
    status === "due" ||
    status === "review"
  );
}

function renderDocumentNeededBadge() {
  return (
    <StatusBadge
      label="Document needed"
      tone="gold"
      icon={FileQuestion}
      iconOnly
      accessibilityLabel="Document needed"
    />
  );
}

function getRecordIcon(record: CostPaymentRecordParam) {
  const kind = getPaymentRecordKind(record);

  if (kind === "claim") {
    return FileCheck2;
  }

  if (kind === "receipt") {
    return Receipt;
  }

  if (kind === "balance" || kind === "payment") {
    return Wallet;
  }

  return FileText;
}

export default function PaymentsClaimsScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const capturePress = usePressCapture("PaymentsClaimsScreen");
  const [selectedRecord, setSelectedRecord] =
    useState<CostPaymentRecordParam | null>(null);
  const [recordOverrides, setRecordOverrides] = useState<
    Record<string, CostPaymentRecordParam>
  >({});
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });
  const paymentRecordsQuery = usePaymentRecords();
  const createPaymentRecordMutation = useCreatePaymentRecord();
  const deletePaymentRecordMutation = useDeletePaymentRecord();
  const updatePaymentRecordMutation = useUpdatePaymentRecord();
  const [addTypePickerVisible, setAddTypePickerVisible] = useState(false);
  const [selectedAddKind, setSelectedAddKind] =
    useState<ManualPaymentKind | null>(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState<string | null>(
    null,
  );
  const [deletedRecordIds, setDeletedRecordIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<PaymentKindFilter>("all");
  const [statusFilter, setStatusFilter] = useState<PaymentStatusFilter>("all");
  // The API ledger is the source of truth. In dev, preserve records passed
  // from the Costs fixture across the stack transition because React
  // Navigation Web drops the query string that enabled the fixture.
  const sessionRecords = paymentRecordsQuery.data ?? [];
  const routeRecords = route.params?.records ?? [];
  // The visual-evidence routes pass deterministic records through navigation.
  // Keep that fixture path isolated to development so production always uses
  // the API response once it has loaded.
  const useDevFixtureRecords =
    __DEV__ &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("dev_costs_payment_record");
  const sourceRecords =
    useDevFixtureRecords || (__DEV__ && routeRecords.length > 0)
      ? routeRecords
      : paymentRecordsQuery.isSuccess
        ? sessionRecords.map((record) => apiToUiPaymentRecord(record, locale))
        : routeRecords;
  const records = sourceRecords
    .filter((record) => !deletedRecordIds.includes(record.id))
    .map((record) =>
      recordOverrides[record.id]
        ? { ...record, ...recordOverrides[record.id] }
        : record,
    );
  const routeSummary = summarizePaymentRecords(routeRecords, 0);
  const routePaid = routeSummary.paid;
  const routeBalances = routeSummary.balances;
  const patientResponsibility =
    route.params?.patientResponsibility ??
    Math.max((route.params?.leftToPay ?? 0) - routeBalances + routePaid, 0);
  const { paid, claims, leftToPay } = summarizePaymentRecords(
    records,
    patientResponsibility,
  );
  const segments = [
    {
      label: "Paid",
      value: paid,
      detail: "Recorded",
      color: PAYMENT_SPLIT_COLORS.paid,
    },
    {
      label: "Claims",
      value: claims,
      detail: "In review",
      color: PAYMENT_SPLIT_COLORS.claims,
    },
    {
      label: "Left",
      value: leftToPay,
      detail: "Expected",
      color: PAYMENT_SPLIT_COLORS.left,
    },
  ].filter((segment) => segment.value > 0);
  const total = Math.max(
    segments.reduce((sum, segment) => sum + Math.max(segment.value, 0), 0),
    1,
  );

  const filteredRecords = filterPaymentRecords(records, {
    query: searchQuery,
    kind: kindFilter,
    status: statusFilter,
  });

  const renderPaymentRecord = (record: CostPaymentRecordParam) => {
    const tone = getPaymentToneStyle(record.status);
    const RecordIcon = getRecordIcon(record);
    const needsDocument = record.documents.some(
      (document) => document.status === "needed",
    );
    const showDocumentBadge =
      needsDocument || record.status === "needs-receipt";
    const showStatusBadge = shouldShowPaymentStatusBadge(record.status);
    const amountLabel =
      record.billedAmount > 0 || record.status === "due"
        ? formatCurrency(record.amount, locale)
        : "Pending";

    return (
      <Row
        surface="plain"
        leading={
          <RecordIcon
            size={17}
            color={PAYMENT_ROW_ICON_COLOR}
            strokeWidth={1.8}
          />
        }
        leadingSize={28}
        title={record.label}
        subtitle={`${record.provider} - ${record.date}`}
        titleRight={
          <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
            {showStatusBadge
              ? renderStatusBadge(tone.label, tone.badgeTone)
              : null}
            {showDocumentBadge ? renderDocumentNeededBadge() : null}
          </View>
        }
        trailing={
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 13,
              fontWeight: "800",
              color: EMBER.colors.text,
              textAlign: "right",
            }}
          >
            {amountLabel}
          </Text>
        }
        onPress={() => setSelectedRecord(record)}
        // Row captures for itself; the visible text is the amount and payee.
        action="payment_claim_row"
        paddingHorizontal={16}
        paddingVertical={12}
        titleStyle={{
          fontSize: 15,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
        subtitleStyle={{
          fontSize: 12,
          color: EMBER.colors.textLight,
          marginTop: 2,
        }}
      />
    );
  };

  const openAddPayment = () => {
    capturePress(pressLabel("Add payment or claim"));
    setAddTypePickerVisible(true);
  };

  const createManualPaymentRecord = async (entry: ManualPaymentEntry) => {
    const status: CreatePaymentRecordPayload["status"] =
      entry.kind === "payment"
        ? "paid"
        : entry.kind === "claim"
          ? "submitted"
          : entry.kind === "receipt"
            ? "needs-receipt"
            : "due";

    if (entry.attachmentName && !entry.attachmentDocumentId) {
      throw new Error("Attached payment document is missing its document ID");
    }

    const refreshedRecords = entry.attachmentUploadId
      ? await paymentRecordsQuery.refetch()
      : null;
    const existing = entry.attachmentUploadId
      ? refreshedRecords?.data?.find((record) =>
          hasCostDocumentUpload(
            record.documents ?? [],
            entry.attachmentUploadId as string,
          ),
        )
      : undefined;
    if (existing) return;

    await createPaymentRecordMutation.mutateAsync({
      kind: entry.kind,
      entrySource: "manual",
      label: entry.label,
      provider: entry.provider,
      amount: toDecimalAmount(entry.amount),
      billedAmount: toDecimalAmount(entry.amount),
      status,
      occurredOn: parseDateText(entry.date) ?? todayIsoDate(),
      note: entry.note || undefined,
      documents: entry.attachmentName
        ? [
            {
              id: entry.attachmentDocumentId as string,
              label: entry.attachmentName,
              status: "attached" as const,
              uploadId: entry.attachmentUploadId ?? null,
            },
          ]
        : [],
    });
  };

  const attachDocumentToRecord = async (document: UploadedCostDocument) => {
    if (!attachmentRecordId) throw new Error("No payment record selected");
    const record = records.find(
      (candidate) => candidate.id === attachmentRecordId,
    );
    if (!record) throw new Error("Payment record is no longer available");
    if (!document.documentId || !document.uploadId) {
      throw new Error("Uploaded document is missing canonical linkage");
    }

    const documents = mergeCostDocumentReferences(
      record.documents,
      toAttachedCostDocumentReference({
        documentId: document.documentId,
        uploadId: document.uploadId,
        name: document.name,
      }),
    );
    const updated = await updatePaymentRecordMutation.mutateAsync({
      paymentRecordId: record.id,
      updates: { documents },
    });
    const mapped = apiToUiPaymentRecord(updated, locale);
    setRecordOverrides((current) => ({ ...current, [record.id]: mapped }));
    setSelectedRecord(mapped);
    setAttachmentRecordId(null);
  };

  const createUploadedPaymentRecord = async (
    document: UploadedCostDocument,
  ) => {
    if (!document.documentId || !document.uploadId) {
      throw new Error("Uploaded document is missing canonical linkage");
    }
    const kind = document.paymentKind ?? "payment";
    const status: CreatePaymentRecordPayload["status"] =
      kind === "payment"
        ? "paid"
        : kind === "claim"
          ? "submitted"
          : kind === "receipt"
            ? "needs-receipt"
            : "due";

    const refreshedRecords = await paymentRecordsQuery.refetch();
    const existing = refreshedRecords.data?.find((record) =>
      hasCostDocumentUpload(record.documents ?? [], document.uploadId!),
    );
    if (existing) {
      setSelectedRecord(apiToUiPaymentRecord(existing, locale));
      return;
    }

    await createPaymentRecordMutation.mutateAsync({
      kind,
      entrySource: "document_upload",
      label: `Uploaded ${kind} document`,
      provider:
        document.source === "camera" ? "Uploaded photo" : "Uploaded file",
      amount: "0",
      billedAmount: "0",
      status,
      occurredOn: todayIsoDate(),
      documents: [
        toAttachedCostDocumentReference({
          documentId: document.documentId,
          uploadId: document.uploadId,
          name: document.name,
        }),
      ],
    });
  };

  const deleteRecord = async (recordId: string) => {
    await deletePaymentRecordMutation.mutateAsync(recordId);
    setDeletedRecordIds((ids) => Array.from(new Set([...ids, recordId])));
    setSelectedRecord(null);
  };

  return (
    <GlassScreenLayout
      overlay={
        <>
          <PaymentEntryTypePicker
            visible={addTypePickerVisible}
            onClose={() => setAddTypePickerVisible(false)}
            onSelect={(kind) => {
              setSelectedAddKind(kind);
              setAddTypePickerVisible(false);
            }}
          />
          <CostDocumentUploadModal
            visible={selectedAddKind != null}
            mode="payment"
            initialPaymentKind={selectedAddKind ?? undefined}
            onClose={() => setSelectedAddKind(null)}
            onDocumentPicked={createUploadedPaymentRecord}
            onManualPaymentAdded={createManualPaymentRecord}
          />
          <CostDocumentUploadModal
            visible={attachmentRecordId != null}
            mode="payment"
            documentOnly
            onClose={() => setAttachmentRecordId(null)}
            onDocumentPicked={attachDocumentToRecord}
            onManualPaymentAdded={async () => undefined}
          />
          {selectedRecord ? (
            <PaymentRecordDetailModal
              record={selectedRecord}
              locale={locale}
              onClose={() => setSelectedRecord(null)}
              onAttachDocument={() => setAttachmentRecordId(selectedRecord.id)}
              onDeleteDocument={async (documentId) => {
                // Detach from the stored ledger row: the UI record's documents
                // carry display text and a synthetic "needed" placeholder.
                const rawRecord = (paymentRecordsQuery.data ?? []).find(
                  (candidate) => candidate.id === selectedRecord.id,
                );
                if (!rawRecord) return;
                const documents = detachDocumentFromPaymentRecord(
                  rawRecord,
                  documentId,
                );
                const updated = await updatePaymentRecordMutation.mutateAsync({
                  paymentRecordId: rawRecord.id,
                  updates: { documents },
                });
                const mapped = apiToUiPaymentRecord(updated, locale);
                setRecordOverrides((recordsById) => ({
                  ...recordsById,
                  [rawRecord.id]: mapped,
                }));
                setSelectedRecord(mapped);
              }}
              onSaveRecord={async (record) => {
                const updated = await updatePaymentRecordMutation.mutateAsync({
                  paymentRecordId: record.id,
                  updates: uiToPaymentRecordUpdates(record),
                });
                const mapped = apiToUiPaymentRecord(updated, locale);
                setRecordOverrides((recordsById) => ({
                  ...recordsById,
                  [record.id]: mapped,
                }));
                setSelectedRecord(mapped);
              }}
              onDeleteRecord={() => deleteRecord(selectedRecord.id)}
              onReviewInsurance={() => {
                setSelectedRecord(null);
                navigation.navigate("Insurance");
              }}
              onOpenFinancing={() => {
                setSelectedRecord(null);
                navigation.navigate("Financing");
              }}
              onOpenTaxTracker={() => {
                setSelectedRecord(null);
                navigation.navigate("TaxReview", {
                  paymentRecordId: selectedRecord.id,
                });
              }}
              onAskChat={(record) => {
                setSelectedRecord(null);
                rootNavigation?.navigate("Chat", {
                  screen: "Assistant",
                  params: {
                    initialPrompt: buildPaymentRecordChatPrompt(record, locale),
                    autoSendInitialPrompt: true,
                  },
                });
              }}
            />
          ) : null}
        </>
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <BackLink
          label={route.params?.returnTab ? t("you.shared.back") : "Costs"}
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        <View style={{ marginBottom: 16 }}>
          <ScreenTitle>Payments and claims</ScreenTitle>
        </View>

        {records.length > 0 && (
          <>
            <CardSurface style={{ padding: 18, gap: 14 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <View style={{ gap: 3 }}>
                  <Text
                    className="font-dm-mono"
                    style={{
                      fontSize: 26,
                      fontWeight: "800",
                      color: EMBER.colors.text,
                    }}
                  >
                    {formatCurrency(leftToPay, locale)}
                  </Text>
                  <CardSubtitleText style={{ marginTop: 0 }}>
                    left to pay for your scenario
                  </CardSubtitleText>
                </View>
              </View>

              {segments.length > 0 ? (
                <>
                  <View
                    style={{
                      height: 12,
                      borderRadius: 999,
                      overflow: "hidden",
                      flexDirection: "row",
                      backgroundColor: "rgba(42, 37, 34, 0.08)",
                    }}
                  >
                    {segments.map((segment, index) => (
                      <View
                        key={`${segment.label}-${index}`}
                        style={{
                          flex: Math.max(segment.value, total * 0.035),
                          backgroundColor: segment.color,
                        }}
                      />
                    ))}
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    {segments.map((segment) => (
                      <View key={segment.label} style={{ flex: 1, gap: 3 }}>
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <View
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: segment.color,
                            }}
                          />
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 12,
                              color: EMBER.colors.textLight,
                              fontWeight: "700",
                            }}
                          >
                            {segment.label}
                          </Text>
                        </View>
                        <Text
                          className="font-dm-mono"
                          style={{
                            fontSize: 13,
                            fontWeight: "800",
                            color: EMBER.colors.text,
                          }}
                        >
                          {formatCurrency(segment.value, locale)}
                        </Text>
                      </View>
                    ))}
                  </View>
                </>
              ) : null}
            </CardSurface>

            <View style={{ height: 18 }} />

            <SectionTitleRow
              title="All payments & claims"
              icon={FileCheck2}
              style={{ marginBottom: 8 }}
            />
          </>
        )}

        {records.length > 0 ? (
          <View style={{ gap: 9, marginBottom: 10 }}>
            <MoonSearchInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Search payments and claims"
              onClear={() => setSearchQuery("")}
              accessibilityLabel="Search payments and claims"
              tone="glass"
            />

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ marginHorizontal: -16 }}
              contentContainerStyle={{
                gap: 8,
                paddingLeft: 16,
                paddingRight: 24,
                paddingBottom: 2,
              }}
            >
              {(
                [
                  ["all", "All"],
                  ["payment", "Payments"],
                  ["claim", "Claims"],
                  ["receipt", "Receipts"],
                  ["balance", "Balances"],
                ] as const
              ).map(([value, label]) => (
                <MoonFilterChip
                  key={value}
                  label={label}
                  accessibilityLabel={`Filter by ${label}`}
                  selected={kindFilter === value}
                  onPress={() => setKindFilter(value)}
                  tone="glass"
                  showCheck={false}
                />
              ))}
            </ScrollView>

            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              {(
                [
                  ["all", "All statuses"],
                  ["needs-attention", "Needs attention"],
                  ["paid", "Paid"],
                ] as const
              ).map(([value, label]) => (
                <MoonFilterChip
                  key={value}
                  label={label}
                  accessibilityLabel={`Filter by ${label}`}
                  selected={statusFilter === value}
                  onPress={() => setStatusFilter(value)}
                  tone="glass"
                  showCheck={false}
                />
              ))}
            </View>
          </View>
        ) : null}

        {records.length === 0 ? (
          <EmptyState
            testID="payments-empty-state"
            density="compact"
            illustration={
              <DialogIcon
                source={paymentEmptyIllustration}
                width={68}
                height={80}
                testID="payments-empty-illustration"
              />
            }
            title="No payments or claims yet"
            titleVariant="featured"
            subtitle="Add payments, claims, receipts, EOBs, and balances from the costs card as treatment starts."
            subtitleStyle={{ maxWidth: 320 }}
            action={
              <CompactActionButton
                label="Add payment or claim"
                variant="primary"
                leadingIcon={Plus}
                onPress={openAddPayment}
              />
            }
            style={{ paddingVertical: 28 }}
          />
        ) : filteredRecords.length === 0 ? (
          <CardSurface style={{ padding: 18 }}>
            <SearchEmptyState
              title={t("empty_states.no_matching_payment_records")}
              onReset={() => {
                setSearchQuery("");
                setKindFilter("all");
                setStatusFilter("all");
              }}
              style={{ paddingVertical: 0, paddingHorizontal: 0 }}
            />
          </CardSurface>
        ) : (
          <CardSurface style={{ overflow: "hidden" }}>
            {filteredRecords.map((record, index) => (
              <View
                key={record.id}
                style={{
                  borderTopWidth: index > 0 ? 1 : 0,
                  borderTopColor: semanticColors.border.subtle,
                }}
              >
                {renderPaymentRecord(record)}
              </View>
            ))}
          </CardSurface>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
